import { ViewConfiguration } from '../../../grid/gridView/types';
import { ColumnDetails } from '../../../grid/types/common';

export interface CustomRulesWebComponentData {
  views: Array<ViewConfiguration<Column, ColumnKeys>>;
  allColumns: Array<ColumnDetails<Column, ColumnKeys>>;
  defaultViewProps: ViewConfiguration<Column, ColumnKeys>;
}

export interface CustomRulesWebComponentSchema {
  data: CustomRulesWebComponentData;
  externalFilters: Array<any>;
}

export enum ColumnKeys {
  RULE_NAME = 'name',
  CREATED_ON = 'created',
  LAST_UPDATED_ON = 'lastModified',
  STATUS = 'status',
  RULE_TYPE = 'type',
  TAGS = 'tags',
  INLINE_ACTIONS = 'inlineActions',
  COMPANY = 'companyId',
}

export enum Column {
  RULE_NAME = 0,
  CREATED_ON = 1,
  LAST_UPDATED_ON = 2,
  STATUS = 3,
  RULE_TYPE = 4,
  TAGS = 5,
  COMPANY = 6,
  INLINE_ACTIONS = 7,
}

export enum Status {
  DISABLED = 0,
  ENABLED = 1,
}

export enum RuleType {
  BASIC = 0,
  YARA = 1,
}

export enum Actions {
  DISABLE = 'disable',
  ENABLE = 'enable',
  DELETE = 'delete',
  SCAN = 'scan',
}

export enum GridType {
  DETECTION = 'detection',
  EXCLUSION = 'exclusion',
}

export enum Severity {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
}

export interface CustomRulesItem {
  targets: object;
  companyId: string;
  availableCompanies: any;
  _id: string;
  name: string;
  created: Date;
  lastModified: Date;
  status: Status;
  type: RuleType;
  company: object;
  canEdit: boolean;
}

export enum CustomRulesEvents {
  INCOMPLETE_DATA = 'incompletedata',
  DELETE = 'delete',
  EDIT = 'edit',
  NOT_UNIQUE = 'notunique',
  EDIT_CRITERIA = 'editcriteria',
}

export enum Type {
  EDR_XDR = -1,
  EDR = 0,
  XDR = 1,
  COMMON = 2,
}

export enum DetectionRuleType {
  PROCESS = 'process',
  FILE = 'file',
  CONNECTION = 'connection',
  REGISTRY = 'registry',
  USER_LOGIN = 'user connection',
  EMAIL = 'email',
  APPLICATION = 'application',
  KEY_VAULT = 'key vault',
  ROLE = 'role',
  POLICY = 'policy',
  SHARING_LINK = 'sharing link',
  FLOW = 'flow',
  URL = 'url',
  SSH_KEY = 'ssh key',
  LAUNCH_TEMPLATE = 'launch template',
  SERVICE_PRINCIPAL = 'service principal',
  USER_GROUP = 'user group',
  AUTOMATION_ACCOUNT = 'automation account',
  AUTOMATION_ACCOUNT_HOOK = 'automation account hook',
  API = 'api',
  CERTIFICATE_AUTHORITY = 'certificate authority',
  BUCKET = 'bucket',
  BITBUCKET_REPOSITORY = 'bitbucket repository',
  JIRA_PROJECT = 'jira project',
  CONFLUENCE_PAGE = 'confluence page',
}

export enum ProcessOptions {
  NAME = 'Name',
  PATH = 'Path',
  FULL_PATH = 'Full path name',
  COMMAND_LINE = 'Command line',
  PARENT_NAME = 'Parent name',
  PARENT_PATH = 'Parent path',
  PARENT_FULL_NAME = 'Parent full path name',
  PARENT_COMMAND_LINE = 'Parent command line',
  USER = 'User',
  MD5 = 'MD5',
  SHA256 = 'SHA2',
  ALERT_NAME = 'Alert name',
}

export enum FileOptions {
  NAME = 'Name',
  PATH = 'Path',
  FULL_PATH = 'Full path name',
  CREATION_PROCESS_NAME = 'Creation process name',
  CREATION_PROCESS_PATH = 'Creation process path',
  CREATION_PROCESS_FULL_PATH = 'Creation process full path name',
  CREATION_PROCESS_COMMAND_LINE = 'Creation process command line',
  OPERATION = 'Operation',
  MD5 = 'MD5',
  SHA256 = 'SHA256',
  URL = 'Url',
  CREATION_PROCESS_USER = 'Creation process user',
  ALERT_NAME = 'Alert name',
}

export enum ConnectionOptions {
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_PORT = 'Source port',
  DESTINATION_PORT = 'Destination port',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  CREATION_PROCESS_NAME = 'Creation process name',
  CREATION_PROCESS_PATH = 'Creation process path',
  CREATION_PROCESS_FULL_PATH = 'Creation process full path name',
  CREATION_PROCESS_COMMAND_LINE = 'Creation process command line',
  CREATION_PROCESS_USER = 'Creation process user',
  URL = 'URL',
  PROTOCOL = 'Protocol',
  HTTP_USER = 'HTTP user',
  HTTP_DOWNLOADED_FILE = 'HTTP downloaded file',
  HTTP_UPLOADED_FILE = 'HTTP uploaded file',
  FTP_USER = 'FTP user',
  SMB_DOMAIN = 'SMB domain',
  SMB_SHARE_PATH = 'SMB share path',
  SMB_USER = 'SMB user',
  SSH_USER = 'SSH user',
  WMI_EXEC_QUERY = 'WMI exec query',
  TELNET_USER = 'Telnet user',
  FILE_REMOTE_OPERATION = 'File remote operation',
  FILE_REMOTE_PATH = 'File remote path',
  FILE_NAME = 'File name',
  EMAIL_SUBJECT = 'Email subject',
  APPLICATION_NAME = 'Application name',
  KEY_VAULT_NAME = 'Key vault name',
  ROLE_NAME = 'Role name',
  POLICY_NAME = 'Policy name',
  SHARING_LINK_NAME = 'Sharing link name',
  FLOW_NAME = 'Flow name',
  URL_NAME = 'Url name',
  SSH_KEY_NAME = 'Ssh key name',
  LAUNCH_TEMPLATE_NAME = 'Launch template name',
  SERVICE_PRINCIPAL_NAME = 'Service principal name',
  USER_GROUP_NAME = 'User group name',
  AUTOMATION_ACCOUNT_NAME = 'Automation account name',
  AUTOMATION_ACCOUNT_HOOK_NAME = 'Automation account hook name',
  API_NAME = 'Api name',
  CERTIFICATE_AUTHORITY_NAME = 'Certificate authority name',
  BUCKET_NAME = 'Bucket name',
  BITBUCKET_REPOSITORY_NAME = 'Bitbucket repository name',
  JIRA_PROJECT_NAME = 'Jira project name',
  CONFLUENCE_PAGE_NAME = 'Confluence page name',
  ALERT_NAME = 'Alert name',
}

export enum RegistryOptions {
  KEY = 'Key',
  VALUE = 'Value',
  OPERATION = 'Operation',
  CREATION_PROCESS_NAME = 'Creation process name',
  CREATION_PROCESS_PATH = 'Creation process path',
  CREATION_PROCESS_FULL_PATH = 'Creation process full path name',
  CREATION_PROCESS_COMMAND_LINE = 'Creation process command line',
  ALERT_NAME = 'Alert name',
}

export enum UserLoginOptions {
  NAME = 'Name',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  DOMAIN = 'Domain',
  FILE_NAME = 'File name',
  EMAIL_SUBJECT = 'Email subject',
  APPLICATION_NAME = 'Application name',
  KEY_VAULT_NAME = 'Key vault name',
  ROLE_NAME = 'Role name',
  POLICY_NAME = 'Policy name',
  SHARING_LINK_NAME = 'Sharing link name',
  FLOW_NAME = 'Flow name',
  URL_NAME = 'Url name',
  SSH_KEY_NAME = 'Ssh key name',
  LAUNCH_TEMPLATE_NAME = 'Launch template name',
  SERVICE_PRINCIPAL_NAME = 'Service principal name',
  USER_GROUP_NAME = 'User group name',
  AUTOMATION_ACCOUNT_NAME = 'Automation account name',
  AUTOMATION_ACCOUNT_HOOK_NAME = 'Automation account hook name',
  API_NAME = 'Api name',
  CERTIFICATE_AUTHORITY_NAME = 'Certificate authority name',
  BUCKET_NAME = 'Bucket name',
  ALERT_NAME = 'Alert name',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  BITBUCKET_REPOSITORY_NAME = 'Bitbucket repository name',
  JIRA_PROJECT_NAME = 'Jira project name',
  CONFLUENCE_PAGE_NAME = 'Confluence page name',
}

export enum EmailOptions {
  SUBJECT = 'Subject',
  SENDER = 'Sender',
  RECEIVER = 'Receiver',
  ATTACHMENT = 'Attachments',
  URL = 'Url',
  ALERT_NAME = 'Alert name',
}

export enum ApplicationOptions {
  NAME = 'Name',
  ID = 'Id',
  APPLICATION_ADDRESS = 'Application address',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum KeyVaultOptions {
  NAME = 'Name',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum RoleOptions {
  NAME = 'Name',
  ID = 'Id',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum PolicyOptions {
  NAME = 'Name',
  ID = 'Id',
  RESOURCE_POLICY_TYPE = 'Resource policy type',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum SharingLinkOptions {
  NAME = 'Name',
  URL = 'Url',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum FlowOptions {
  NAME = 'Name',
  ID = 'Id',
  URL = 'Url',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum UrlOptions {
  NAME = 'Name',
  URL = 'Url',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum SshKeyOptions {
  NAME = 'Name',
  SSH_PUBLIC_KEY = 'Ssh public key',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum LaunchTemplateOptions {
  NAME = 'Name',
  ID = 'Id',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum ServicePrincipalOptions {
  NAME = 'Name',
  ID = 'Id',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum UserGroupOptions {
  NAME = 'Name',
  ID = 'Id',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum AutomationAccountOptions {
  NAME = 'Name',
  ID = 'Id',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum AutomationAccountHookOptions {
  NAME = 'Name',
  ID = 'Id',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum ApiOptions {
  NAME = 'Name',
  ID = 'Id',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum CertificateAuthorityOptions {
  NAME = 'Name',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum BucketOptions {
  NAME = 'Name',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  SOURCE_USER = 'Source user',
  DESTINATION_USER = 'Destination user',
  ALERT_NAME = 'Alert name',
}

export enum BitbucketRepositoryOptions {
  NAME = 'Name',
  ID = 'Id',
  URL = 'URL',
  SOURCE_USER = 'Source User',
  DESTINATION_USER = 'Destination user',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  ALERT_NAME = 'Alert name',
}

export enum JiraProjectOptions {
  NAME = 'Name',
  ID = 'Id',
  DOMAIN = 'Domain',
  SOURCE_USER = 'Source User',
  DESTINATION_USER = 'Destination user',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  ALERT_NAME = 'Alert name',
}

export enum ConfluencePageOptions {
  NAME = 'Name',
  ID = 'Id',
  URL = 'URL',
  SOURCE_USER = 'Source User',
  DESTINATION_USER = 'Destination user',
  SOURCE_IP = 'Source IP',
  DESTINATION_IP = 'Destination IP',
  ALERT_NAME = 'Alert name',
}

export enum Operators {
  IS = 0,
  CONTAINS = 1,
  IS_ONE_OF = 2,
}

export enum Targets {
  COMPANY = 0,
  ENDPOINT = 1,
  COMPANIES = 2,
}

export enum FilterFields {
  ALERT_NAME = 'Alert name'
}

export const ALERT_NAME = 'Alert name';

export const ruleFields = {
  [DetectionRuleType.PROCESS]: {
    [ProcessOptions.NAME]: 'Process.Name',
    [ProcessOptions.PATH]: 'Process.Path',
    [ProcessOptions.FULL_PATH]: 'Process.FullPathName',
    [ProcessOptions.COMMAND_LINE]: 'Process.CommandLine',
    [ProcessOptions.PARENT_NAME]: 'Process.Parent.Name',
    [ProcessOptions.PARENT_PATH]: 'Process.Parent.Path',
    [ProcessOptions.PARENT_FULL_NAME]: 'Process.Parent.FullPathName',
    [ProcessOptions.PARENT_COMMAND_LINE]: 'Process.Parent.CommandLine',
    [ProcessOptions.USER]: 'Process.User',
    [ProcessOptions.MD5]: 'Process.MD5',
    [ProcessOptions.SHA256]: 'Process.SHA2',
  },
  [DetectionRuleType.FILE]: {
    [FileOptions.NAME]: 'File.Name',
    [FileOptions.PATH]: 'File.Path',
    [FileOptions.FULL_PATH]: 'File.FullPathName',
    [FileOptions.CREATION_PROCESS_NAME]: 'File.CreatedBy.Name',
    [FileOptions.CREATION_PROCESS_PATH]: 'File.CreatedBy.Path',
    [FileOptions.CREATION_PROCESS_FULL_PATH]: 'File.CreatedBy.FullPathName',
    [FileOptions.CREATION_PROCESS_COMMAND_LINE]: 'File.CreatedBy.CommandLine',
    [FileOptions.OPERATION]: 'File.Operation',
    [FileOptions.MD5]: 'File.MD5',
    [FileOptions.SHA256]: 'File.SHA2',
    [FileOptions.URL]: 'File.Url',
    [FileOptions.CREATION_PROCESS_USER]: 'File.CreatedBy.User',
  },
  [DetectionRuleType.CONNECTION]: {
    [ConnectionOptions.SOURCE_IP]: 'Connection.SourceIP',
    [ConnectionOptions.DESTINATION_IP]: 'Connection.DestinationIP',
    [ConnectionOptions.SOURCE_PORT]: 'Connection.SourcePort',
    [ConnectionOptions.DESTINATION_PORT]: 'Connection.DestinationPort',
    [ConnectionOptions.SOURCE_USER]: 'Connection.SourceUser',
    [ConnectionOptions.DESTINATION_USER]: 'Connection.DestinationUser',
    [ConnectionOptions.CREATION_PROCESS_NAME]: 'Connection.Process.Name',
    [ConnectionOptions.CREATION_PROCESS_PATH]: 'Connection.Process.Path',
    [ConnectionOptions.CREATION_PROCESS_FULL_PATH]: 'Connection.Process.FullPathName',
    [ConnectionOptions.CREATION_PROCESS_COMMAND_LINE]: 'Connection.Process.CommandLine',
    [ConnectionOptions.CREATION_PROCESS_USER]: 'Connection.Process.User',
    [ConnectionOptions.PROTOCOL]: 'Connection.Protocol',
    [ConnectionOptions.URL]: 'Connection.URL',
    [ConnectionOptions.HTTP_USER]: 'Connection.HTTPUser',
    [ConnectionOptions.HTTP_DOWNLOADED_FILE]: 'Connection.HTTPDownloadedFile',
    [ConnectionOptions.HTTP_UPLOADED_FILE]: 'Connection.HTTPUploadedFile',
    [ConnectionOptions.FTP_USER]: 'Connection.FTPUser',
    [ConnectionOptions.SMB_DOMAIN]: 'Connection.SMBDomain',
    [ConnectionOptions.SMB_SHARE_PATH]: 'Connection.SMBSharePath',
    [ConnectionOptions.SMB_USER]: 'Connection.SMBUser',
    [ConnectionOptions.SSH_USER]: 'Connection.SSHUser',
    [ConnectionOptions.WMI_EXEC_QUERY]: 'Connection.WMIExecQuery',
    [ConnectionOptions.TELNET_USER]: 'Connection.TelnetUser',
    [ConnectionOptions.FILE_REMOTE_OPERATION]: 'Connection.FileRemoteOperation',
    [ConnectionOptions.FILE_REMOTE_PATH]: 'Connection.FileRemotePath',
    [ConnectionOptions.FILE_NAME]: 'Connection.File.Name',
    [ConnectionOptions.EMAIL_SUBJECT]: 'Connection.Email.Subject',
    [ConnectionOptions.APPLICATION_NAME]: 'Connection.Application.Name',
    [ConnectionOptions.KEY_VAULT_NAME]: 'Connection.KeyVault.Name',
    [ConnectionOptions.ROLE_NAME]: 'Connection.Role.Name',
    [ConnectionOptions.POLICY_NAME]: 'Connection.Policy.Name',
    [ConnectionOptions.SHARING_LINK_NAME]: 'Connection.SharingLink.Name',
    [ConnectionOptions.FLOW_NAME]: 'Connection.Flow.Name',
    [ConnectionOptions.URL_NAME]: 'Connection.Url.Name',
    [ConnectionOptions.SSH_KEY_NAME]: 'Connection.SshKey.Name',
    [ConnectionOptions.LAUNCH_TEMPLATE_NAME]: 'Connection.LaunchTemplate.Name',
    [ConnectionOptions.SERVICE_PRINCIPAL_NAME]: 'Connection.ServicePrincipal.Name',
    [ConnectionOptions.USER_GROUP_NAME]: 'Connection.UserGroup.Name',
    [ConnectionOptions.AUTOMATION_ACCOUNT_NAME]: 'Connection.AutomationAccount.Name',
    [ConnectionOptions.AUTOMATION_ACCOUNT_HOOK_NAME]: 'Connection.AutomationAccountHook.Name',
    [ConnectionOptions.API_NAME]: 'Connection.Api.Name',
    [ConnectionOptions.CERTIFICATE_AUTHORITY_NAME]: 'Connection.CertificateAuthority.Name',
    [ConnectionOptions.BUCKET_NAME]: 'Connection.Bucket.Name',
    [ConnectionOptions.BITBUCKET_REPOSITORY_NAME]: 'Connection.BitbucketRepository.Name',
    [ConnectionOptions.JIRA_PROJECT_NAME]: 'Connection.JiraProject.Name',
    [ConnectionOptions.CONFLUENCE_PAGE_NAME]: 'Connection.ConfluencePage.Name',
  },
  [DetectionRuleType.REGISTRY]: {
    [RegistryOptions.KEY]: 'Registry.Key',
    [RegistryOptions.VALUE]: 'Registry.Value',
    [RegistryOptions.OPERATION]: 'Registry.Operation',
    [RegistryOptions.CREATION_PROCESS_NAME]: 'Registry.CreatedBy.Name',
    [RegistryOptions.CREATION_PROCESS_PATH]: 'Registry.CreatedBy.Path',
    [RegistryOptions.CREATION_PROCESS_FULL_PATH]: 'Registry.CreatedBy.FullPathName',
    [RegistryOptions.CREATION_PROCESS_COMMAND_LINE]: 'Registry.CreatedBy.CommandLine',
  },
  [DetectionRuleType.USER_LOGIN]: {
    [UserLoginOptions.NAME]: 'UserLogin.Name',
    [UserLoginOptions.SOURCE_USER]: 'UserLogin.SourceUser',
    [UserLoginOptions.DESTINATION_USER]: 'UserLogin.DestinationUser',
    [UserLoginOptions.DOMAIN]: 'UserLogin.Domain',
    [UserLoginOptions.SOURCE_IP]: 'UserLogin.SourceIP',
    [UserLoginOptions.DESTINATION_IP]: 'UserLogin.DestinationIP',
    [UserLoginOptions.FILE_NAME]: 'UserLogin.File.Name',
    [UserLoginOptions.EMAIL_SUBJECT]: 'UserLogin.Email.Subject',
    [UserLoginOptions.APPLICATION_NAME]: 'UserLogin.Application.Name',
    [UserLoginOptions.KEY_VAULT_NAME]: 'UserLogin.KeyVault.Name',
    [UserLoginOptions.ROLE_NAME]: 'UserLogin.Role.Name',
    [UserLoginOptions.POLICY_NAME]: 'UserLogin.Policy.Name',
    [UserLoginOptions.SHARING_LINK_NAME]: 'UserLogin.SharingLink.Name',
    [UserLoginOptions.FLOW_NAME]: 'UserLogin.Flow.Name',
    [UserLoginOptions.URL_NAME]: 'UserLogin.Url.Name',
    [UserLoginOptions.SSH_KEY_NAME]: 'UserLogin.SshKey.Name',
    [UserLoginOptions.LAUNCH_TEMPLATE_NAME]: 'UserLogin.LaunchTemplate.Name',
    [UserLoginOptions.SERVICE_PRINCIPAL_NAME]: 'UserLogin.ServicePrincipal.Name',
    [UserLoginOptions.USER_GROUP_NAME]: 'UserLogin.UserGroup.Name',
    [UserLoginOptions.AUTOMATION_ACCOUNT_NAME]: 'UserLogin.AutomationAccount.Name',
    [UserLoginOptions.AUTOMATION_ACCOUNT_HOOK_NAME]: 'UserLogin.AutomationAccountHook.Name',
    [UserLoginOptions.API_NAME]: 'UserLogin.Api.Name',
    [UserLoginOptions.CERTIFICATE_AUTHORITY_NAME]: 'UserLogin.CertificateAuthority.Name',
    [UserLoginOptions.BUCKET_NAME]: 'UserLogin.Bucket.Name',
    [UserLoginOptions.BITBUCKET_REPOSITORY_NAME]: 'UserLogin.BitbucketRepository.Name',
    [UserLoginOptions.JIRA_PROJECT_NAME]: 'UserLogin.JiraProject.Name',
    [UserLoginOptions.CONFLUENCE_PAGE_NAME]: 'UserLogin.ConfluencePage.Name',
  },
  [DetectionRuleType.EMAIL]: {
    [EmailOptions.SUBJECT]: 'Email.Subject',
    [EmailOptions.SENDER]: 'Email.Sender',
    [EmailOptions.RECEIVER]: 'Email.Receivers',
    [EmailOptions.ATTACHMENT]: 'Email.Attachments',
    [EmailOptions.URL]: 'Email.Url',
  },
  [DetectionRuleType.APPLICATION]: {
    [ApplicationOptions.NAME]: 'Application.Name',
    [ApplicationOptions.ID]: 'Application.Id',
    [ApplicationOptions.APPLICATION_ADDRESS]: 'Application.Address',
    [ApplicationOptions.SOURCE_IP]: 'Application.SourceIP',
    [ApplicationOptions.DESTINATION_IP]: 'Application.DestinationIP',
    [ApplicationOptions.SOURCE_USER]: 'Application.SourceUser',
    [ApplicationOptions.DESTINATION_USER]: 'Application.DestinationUser',
  },
  [DetectionRuleType.KEY_VAULT]: {
    [KeyVaultOptions.NAME]: 'KeyVault.Name',
    [KeyVaultOptions.SOURCE_IP]: 'KeyVault.SourceIP',
    [KeyVaultOptions.DESTINATION_IP]: 'KeyVault.DestinationIP',
    [KeyVaultOptions.SOURCE_USER]: 'KeyVault.SourceUser',
    [KeyVaultOptions.DESTINATION_USER]: 'KeyVault.DestinationUser',
  },
  [DetectionRuleType.ROLE]: {
    [RoleOptions.NAME]: 'Role.Name',
    [RoleOptions.ID]: 'Role.Id',
    [RoleOptions.SOURCE_IP]: 'Role.SourceIP',
    [RoleOptions.DESTINATION_IP]: 'Role.DestinationIP',
    [RoleOptions.SOURCE_USER]: 'Role.SourceUser',
    [RoleOptions.DESTINATION_USER]: 'Role.DestinationUser',
  },
  [DetectionRuleType.POLICY]: {
    [PolicyOptions.NAME]: 'Policy.Name',
    [PolicyOptions.ID]: 'Policy.Id',
    [PolicyOptions.RESOURCE_POLICY_TYPE]: 'Policy.ResourcePolicyType',
    [PolicyOptions.SOURCE_IP]: 'Policy.SourceIP',
    [PolicyOptions.DESTINATION_IP]: 'Policy.DestinationIP',
    [PolicyOptions.SOURCE_USER]: 'Policy.SourceUser',
    [PolicyOptions.DESTINATION_USER]: 'Policy.DestinationUser',
  },
  [DetectionRuleType.SHARING_LINK]: {
    [SharingLinkOptions.NAME]: 'SharingLink.Name',
    [SharingLinkOptions.URL]: 'SharingLink.Url',
    [SharingLinkOptions.SOURCE_IP]: 'SharingLink.SourceIP',
    [SharingLinkOptions.DESTINATION_IP]: 'SharingLink.DestinationIP',
    [SharingLinkOptions.SOURCE_USER]: 'SharingLink.SourceUser',
    [SharingLinkOptions.DESTINATION_USER]: 'SharingLink.DestinationUser',
  },
  [DetectionRuleType.FLOW]: {
    [FlowOptions.NAME]: 'Flow.Name',
    [FlowOptions.ID]: 'Flow.Id',
    [FlowOptions.URL]: 'Flow.Url',
    [FlowOptions.SOURCE_IP]: 'Flow.SourceIP',
    [FlowOptions.DESTINATION_IP]: 'Flow.DestinationIP',
    [FlowOptions.SOURCE_USER]: 'Flow.SourceUser',
    [FlowOptions.DESTINATION_USER]: 'Flow.DestinationUser',
  },
  [DetectionRuleType.URL]: {
    [UrlOptions.NAME]: 'Url.Name',
    [UrlOptions.URL]: 'Url.Url',
    [UrlOptions.SOURCE_IP]: 'Url.SourceIP',
    [UrlOptions.DESTINATION_IP]: 'Url.DestinationIP',
    [UrlOptions.SOURCE_USER]: 'Url.SourceUser',
    [UrlOptions.DESTINATION_USER]: 'Url.DestinationUser',
  },
  [DetectionRuleType.SSH_KEY]: {
    [SshKeyOptions.NAME]: 'SshKey.Name',
    [SshKeyOptions.SSH_PUBLIC_KEY]: 'SshKey.PublicKey',
    [SshKeyOptions.SOURCE_IP]: 'SshKey.SourceIP',
    [SshKeyOptions.DESTINATION_IP]: 'SshKey.DestinationIP',
    [SshKeyOptions.SOURCE_USER]: 'SshKey.SourceUser',
    [SshKeyOptions.DESTINATION_USER]: 'SshKey.DestinationUser',
  },
  [DetectionRuleType.LAUNCH_TEMPLATE]: {
    [LaunchTemplateOptions.NAME]: 'LaunchTemplate.Name',
    [LaunchTemplateOptions.ID]: 'LaunchTemplate.Id',
    [LaunchTemplateOptions.SOURCE_IP]: 'LaunchTemplate.SourceIP',
    [LaunchTemplateOptions.DESTINATION_IP]: 'LaunchTemplate.DestinationIP',
    [LaunchTemplateOptions.SOURCE_USER]: 'LaunchTemplate.SourceUser',
    [LaunchTemplateOptions.DESTINATION_USER]: 'LaunchTemplate.DestinationUser',
  },
  [DetectionRuleType.SERVICE_PRINCIPAL]: {
    [ServicePrincipalOptions.NAME]: 'ServicePrincipal.Name',
    [ServicePrincipalOptions.ID]: 'ServicePrincipal.Id',
    [ServicePrincipalOptions.SOURCE_IP]: 'ServicePrincipal.SourceIP',
    [ServicePrincipalOptions.DESTINATION_IP]: 'ServicePrincipal.DestinationIP',
    [ServicePrincipalOptions.SOURCE_USER]: 'ServicePrincipal.SourceUser',
    [ServicePrincipalOptions.DESTINATION_USER]: 'ServicePrincipal.DestinationUser',
  },
  [DetectionRuleType.USER_GROUP]: {
    [UserGroupOptions.NAME]: 'UserGroup.Name',
    [UserGroupOptions.ID]: 'UserGroup.Id',
    [UserGroupOptions.SOURCE_IP]: 'UserGroup.SourceIP',
    [UserGroupOptions.DESTINATION_IP]: 'UserGroup.DestinationIP',
    [UserGroupOptions.SOURCE_USER]: 'UserGroup.SourceUser',
    [UserGroupOptions.DESTINATION_USER]: 'UserGroup.DestinationUser',
  },
  [DetectionRuleType.AUTOMATION_ACCOUNT]: {
    [AutomationAccountOptions.NAME]: 'AutomationAccount.Name',
    [AutomationAccountOptions.ID]: 'AutomationAccount.Id',
    [AutomationAccountOptions.SOURCE_IP]: 'AutomationAccount.SourceIP',
    [AutomationAccountOptions.DESTINATION_IP]: 'AutomationAccount.DestinationIP',
    [AutomationAccountOptions.SOURCE_USER]: 'AutomationAccount.SourceUser',
    [AutomationAccountOptions.DESTINATION_USER]: 'AutomationAccount.DestinationUser',
  },
  [DetectionRuleType.AUTOMATION_ACCOUNT_HOOK]: {
    [AutomationAccountHookOptions.NAME]: 'AutomationAccountHook.Name',
    [AutomationAccountHookOptions.ID]: 'AutomationAccountHook.Id',
    [AutomationAccountHookOptions.SOURCE_IP]: 'AutomationAccountHook.SourceIP',
    [AutomationAccountHookOptions.DESTINATION_IP]: 'AutomationAccountHook.DestinationIP',
    [AutomationAccountHookOptions.SOURCE_USER]: 'AutomationAccountHook.SourceUser',
    [AutomationAccountHookOptions.DESTINATION_USER]: 'AutomationAccountHook.DestinationUser',
  },
  [DetectionRuleType.API]: {
    [ApiOptions.NAME]: 'Api.Name',
    [ApiOptions.ID]: 'Api.Id',
    [ApiOptions.SOURCE_IP]: 'Api.SourceIP',
    [ApiOptions.DESTINATION_IP]: 'Api.DestinationIP',
    [ApiOptions.SOURCE_USER]: 'Api.SourceUser',
    [ApiOptions.DESTINATION_USER]: 'Api.DestinationUser',
  },
  [DetectionRuleType.CERTIFICATE_AUTHORITY]: {
    [CertificateAuthorityOptions.NAME]: 'CertificateAuthority.Name',
    [CertificateAuthorityOptions.SOURCE_IP]: 'CertificateAuthority.SourceIP',
    [CertificateAuthorityOptions.DESTINATION_IP]: 'CertificateAuthority.DestinationIP',
    [CertificateAuthorityOptions.SOURCE_USER]: 'CertificateAuthority.SourceUser',
    [CertificateAuthorityOptions.DESTINATION_USER]: 'CertificateAuthority.DestinationUser',
  },
  [DetectionRuleType.BUCKET]: {
    [BucketOptions.NAME]: 'Bucket.Name',
    [BucketOptions.SOURCE_IP]: 'Bucket.SourceIP',
    [BucketOptions.DESTINATION_IP]: 'Bucket.DestinationIP',
    [BucketOptions.SOURCE_USER]: 'Bucket.SourceUser',
    [BucketOptions.DESTINATION_USER]: 'Bucket.DestinationUser',
  },
  [DetectionRuleType.BITBUCKET_REPOSITORY]: {
    [BitbucketRepositoryOptions.NAME]: 'BitbucketRepository.Name',
    [BitbucketRepositoryOptions.ID]: 'BitbucketRepository.Id',
    [BitbucketRepositoryOptions.URL]: 'BitbucketRepository.Url',
    [BitbucketRepositoryOptions.SOURCE_USER]: 'BitbucketRepository.SourceUser',
    [BitbucketRepositoryOptions.DESTINATION_USER]: 'BitbucketRepository.DestinationUser',
    [BitbucketRepositoryOptions.SOURCE_IP]: 'BitbucketRepository.SourceIP',
    [BitbucketRepositoryOptions.DESTINATION_IP]: 'BitbucketRepository.DestinationIP',
  },
  [DetectionRuleType.JIRA_PROJECT]: {
    [JiraProjectOptions.NAME]: 'JiraProject.Name',
    [JiraProjectOptions.ID]: 'JiraProject.Id',
    [JiraProjectOptions.DOMAIN]: 'JiraProject.Domain',
    [JiraProjectOptions.SOURCE_USER]: 'JiraProject.SourceUser',
    [JiraProjectOptions.DESTINATION_USER]: 'JiraProject.DestinationUser',
    [JiraProjectOptions.SOURCE_IP]: 'JiraProject.SourceIP',
    [JiraProjectOptions.DESTINATION_IP]: 'JiraProject.DestinationIP',
  },
  [DetectionRuleType.CONFLUENCE_PAGE]: {
    [ConfluencePageOptions.NAME]: 'ConfluencePage.Name',
    [ConfluencePageOptions.ID]: 'ConfluencePage.Id',
    [ConfluencePageOptions.URL]: 'ConfluencePage.Url',
    [ConfluencePageOptions.SOURCE_USER]: 'ConfluencePage.SourceUser',
    [ConfluencePageOptions.DESTINATION_USER]: 'ConfluencePage.DestinationUser',
    [ConfluencePageOptions.SOURCE_IP]: 'ConfluencePage.SourceIP',
    [ConfluencePageOptions.DESTINATION_IP]: 'ConfluencePage.DestinationIP',
  },
};

export const detectionOptions = {
  [DetectionRuleType.PROCESS]: [
    ProcessOptions.NAME,
    ProcessOptions.PATH,
    ProcessOptions.FULL_PATH,
    ProcessOptions.COMMAND_LINE,
    ProcessOptions.PARENT_NAME,
    ProcessOptions.PARENT_PATH,
    ProcessOptions.PARENT_FULL_NAME,
    ProcessOptions.PARENT_COMMAND_LINE,
  ],
  [DetectionRuleType.FILE]: [
    FileOptions.NAME,
    FileOptions.PATH,
    FileOptions.FULL_PATH,
    FileOptions.CREATION_PROCESS_NAME,
    FileOptions.CREATION_PROCESS_PATH,
    FileOptions.CREATION_PROCESS_FULL_PATH,
    FileOptions.CREATION_PROCESS_COMMAND_LINE,
  ],
  [DetectionRuleType.CONNECTION]: [
    ConnectionOptions.SOURCE_IP,
    ConnectionOptions.DESTINATION_IP,
    ConnectionOptions.SOURCE_PORT,
    ConnectionOptions.DESTINATION_PORT,
    ConnectionOptions.CREATION_PROCESS_NAME,
    ConnectionOptions.CREATION_PROCESS_PATH,
    ConnectionOptions.CREATION_PROCESS_FULL_PATH,
    ConnectionOptions.CREATION_PROCESS_COMMAND_LINE,
  ],
  [DetectionRuleType.REGISTRY]: [
    RegistryOptions.KEY,
    RegistryOptions.VALUE,
    RegistryOptions.OPERATION,
    RegistryOptions.CREATION_PROCESS_NAME,
    RegistryOptions.CREATION_PROCESS_PATH,
    RegistryOptions.CREATION_PROCESS_FULL_PATH,
    RegistryOptions.CREATION_PROCESS_COMMAND_LINE,
  ],
};

export const exclusionOptions = {
  [DetectionRuleType.PROCESS]: [
    ProcessOptions.NAME,
    ProcessOptions.PATH,
    ProcessOptions.FULL_PATH,
    ProcessOptions.COMMAND_LINE,
    ProcessOptions.PARENT_NAME,
    ProcessOptions.PARENT_PATH,
    ProcessOptions.PARENT_FULL_NAME,
    ProcessOptions.PARENT_COMMAND_LINE,
    ProcessOptions.USER,
    ProcessOptions.MD5,
    ProcessOptions.SHA256,
    ProcessOptions.ALERT_NAME,
  ],
  [DetectionRuleType.FILE]: [
    FileOptions.NAME,
    FileOptions.PATH,
    FileOptions.FULL_PATH,
    FileOptions.CREATION_PROCESS_NAME,
    FileOptions.CREATION_PROCESS_PATH,
    FileOptions.CREATION_PROCESS_FULL_PATH,
    FileOptions.CREATION_PROCESS_COMMAND_LINE,
    FileOptions.OPERATION,
    FileOptions.MD5,
    FileOptions.SHA256,
    FileOptions.URL,
    FileOptions.CREATION_PROCESS_USER,
    FileOptions.ALERT_NAME,
  ],
  [DetectionRuleType.CONNECTION]: [
    ConnectionOptions.SOURCE_IP,
    ConnectionOptions.DESTINATION_IP,
    ConnectionOptions.SOURCE_PORT,
    ConnectionOptions.DESTINATION_PORT,
    ConnectionOptions.SOURCE_USER,
    ConnectionOptions.DESTINATION_USER,
    ConnectionOptions.CREATION_PROCESS_NAME,
    ConnectionOptions.CREATION_PROCESS_PATH,
    ConnectionOptions.CREATION_PROCESS_FULL_PATH,
    ConnectionOptions.CREATION_PROCESS_COMMAND_LINE,
    ConnectionOptions.CREATION_PROCESS_USER,
    ConnectionOptions.URL,
    ConnectionOptions.HTTP_USER,
    ConnectionOptions.HTTP_DOWNLOADED_FILE,
    ConnectionOptions.HTTP_UPLOADED_FILE,
    ConnectionOptions.FTP_USER,
    ConnectionOptions.SMB_DOMAIN,
    ConnectionOptions.SMB_SHARE_PATH,
    ConnectionOptions.SMB_USER,
    ConnectionOptions.SSH_USER,
    ConnectionOptions.WMI_EXEC_QUERY,
    ConnectionOptions.TELNET_USER,
    ConnectionOptions.FILE_REMOTE_OPERATION,
    ConnectionOptions.FILE_REMOTE_PATH,
    ConnectionOptions.FILE_NAME,
    ConnectionOptions.EMAIL_SUBJECT,
    ConnectionOptions.APPLICATION_NAME,
    ConnectionOptions.KEY_VAULT_NAME,
    ConnectionOptions.ROLE_NAME,
    ConnectionOptions.POLICY_NAME,
    ConnectionOptions.SHARING_LINK_NAME,
    ConnectionOptions.FLOW_NAME,
    ConnectionOptions.URL_NAME,
    ConnectionOptions.SSH_KEY_NAME,
    ConnectionOptions.LAUNCH_TEMPLATE_NAME,
    ConnectionOptions.SERVICE_PRINCIPAL_NAME,
    ConnectionOptions.USER_GROUP_NAME,
    ConnectionOptions.AUTOMATION_ACCOUNT_NAME,
    ConnectionOptions.AUTOMATION_ACCOUNT_HOOK_NAME,
    ConnectionOptions.API_NAME,
    ConnectionOptions.CERTIFICATE_AUTHORITY_NAME,
    ConnectionOptions.BUCKET_NAME,
    ConnectionOptions.ALERT_NAME,
    ConnectionOptions.BITBUCKET_REPOSITORY_NAME,
    ConnectionOptions.JIRA_PROJECT_NAME,
    ConnectionOptions.CONFLUENCE_PAGE_NAME
  ],
  [DetectionRuleType.REGISTRY]: [
    RegistryOptions.KEY,
    RegistryOptions.VALUE,
    RegistryOptions.OPERATION,
    RegistryOptions.CREATION_PROCESS_NAME,
    RegistryOptions.CREATION_PROCESS_PATH,
    RegistryOptions.CREATION_PROCESS_FULL_PATH,
    RegistryOptions.CREATION_PROCESS_COMMAND_LINE,
    RegistryOptions.ALERT_NAME,
  ],
  [DetectionRuleType.USER_LOGIN]: [
    UserLoginOptions.NAME,
    UserLoginOptions.SOURCE_USER,
    UserLoginOptions.DESTINATION_USER,
    UserLoginOptions.DOMAIN,
    UserLoginOptions.FILE_NAME,
    UserLoginOptions.EMAIL_SUBJECT,
    UserLoginOptions.APPLICATION_NAME,
    UserLoginOptions.KEY_VAULT_NAME,
    UserLoginOptions.ROLE_NAME,
    UserLoginOptions.POLICY_NAME,
    UserLoginOptions.SHARING_LINK_NAME,
    UserLoginOptions.FLOW_NAME,
    UserLoginOptions.URL_NAME,
    UserLoginOptions.SSH_KEY_NAME,
    UserLoginOptions.LAUNCH_TEMPLATE_NAME,
    UserLoginOptions.SERVICE_PRINCIPAL_NAME,
    UserLoginOptions.USER_GROUP_NAME,
    UserLoginOptions.AUTOMATION_ACCOUNT_NAME,
    UserLoginOptions.AUTOMATION_ACCOUNT_HOOK_NAME,
    UserLoginOptions.API_NAME,
    UserLoginOptions.CERTIFICATE_AUTHORITY_NAME,
    UserLoginOptions.BUCKET_NAME,
    UserLoginOptions.ALERT_NAME,
    UserLoginOptions.SOURCE_IP,
    UserLoginOptions.DESTINATION_IP,
    ConnectionOptions.BITBUCKET_REPOSITORY_NAME,
    ConnectionOptions.JIRA_PROJECT_NAME,
    ConnectionOptions.CONFLUENCE_PAGE_NAME
  ],
  [DetectionRuleType.EMAIL]: [
    EmailOptions.SUBJECT,
    EmailOptions.SENDER,
    EmailOptions.RECEIVER,
    EmailOptions.ATTACHMENT,
    EmailOptions.URL,
    EmailOptions.ALERT_NAME,
  ],
  [DetectionRuleType.APPLICATION]: [
    ApplicationOptions.NAME,
    ApplicationOptions.ID,
    ApplicationOptions.APPLICATION_ADDRESS,
    ApplicationOptions.SOURCE_IP,
    ApplicationOptions.DESTINATION_IP,
    ApplicationOptions.SOURCE_USER,
    ApplicationOptions.DESTINATION_USER,
    ApplicationOptions.ALERT_NAME,
  ],
  [DetectionRuleType.KEY_VAULT]: [
    KeyVaultOptions.NAME,
    KeyVaultOptions.SOURCE_IP,
    KeyVaultOptions.DESTINATION_IP,
    KeyVaultOptions.SOURCE_USER,
    KeyVaultOptions.DESTINATION_USER,
    KeyVaultOptions.ALERT_NAME,
  ],
  [DetectionRuleType.ROLE]: [
    RoleOptions.NAME,
    RoleOptions.ID,
    RoleOptions.SOURCE_IP,
    RoleOptions.DESTINATION_IP,
    RoleOptions.SOURCE_USER,
    RoleOptions.DESTINATION_USER,
    RoleOptions.ALERT_NAME,
  ],
  [DetectionRuleType.POLICY]: [
    PolicyOptions.NAME,
    PolicyOptions.ID,
    PolicyOptions.RESOURCE_POLICY_TYPE,
    PolicyOptions.SOURCE_IP,
    PolicyOptions.DESTINATION_IP,
    PolicyOptions.SOURCE_USER,
    PolicyOptions.DESTINATION_USER,
    PolicyOptions.ALERT_NAME,
  ],
  [DetectionRuleType.SHARING_LINK]: [
    SharingLinkOptions.NAME,
    SharingLinkOptions.URL,
    SharingLinkOptions.SOURCE_IP,
    SharingLinkOptions.DESTINATION_IP,
    SharingLinkOptions.SOURCE_USER,
    SharingLinkOptions.DESTINATION_USER,
    SharingLinkOptions.ALERT_NAME,
  ],
  [DetectionRuleType.FLOW]: [
    FlowOptions.NAME,
    FlowOptions.ID,
    FlowOptions.URL,
    FlowOptions.SOURCE_IP,
    FlowOptions.DESTINATION_IP,
    FlowOptions.SOURCE_USER,
    FlowOptions.DESTINATION_USER,
    FlowOptions.ALERT_NAME,
  ],
  [DetectionRuleType.URL]: [
    UrlOptions.NAME,
    UrlOptions.URL,
    UrlOptions.SOURCE_IP,
    UrlOptions.DESTINATION_IP,
    UrlOptions.SOURCE_USER,
    UrlOptions.DESTINATION_USER,
    UrlOptions.ALERT_NAME,
  ],
  [DetectionRuleType.SSH_KEY]: [
    SshKeyOptions.NAME,
    SshKeyOptions.SSH_PUBLIC_KEY,
    SshKeyOptions.SOURCE_IP,
    SshKeyOptions.DESTINATION_IP,
    SshKeyOptions.SOURCE_USER,
    SshKeyOptions.DESTINATION_USER,
    SshKeyOptions.ALERT_NAME,
  ],
  [DetectionRuleType.LAUNCH_TEMPLATE]: [
    LaunchTemplateOptions.NAME,
    LaunchTemplateOptions.ID,
    LaunchTemplateOptions.SOURCE_IP,
    LaunchTemplateOptions.DESTINATION_IP,
    LaunchTemplateOptions.SOURCE_USER,
    LaunchTemplateOptions.DESTINATION_USER,
    LaunchTemplateOptions.ALERT_NAME,
  ],
  [DetectionRuleType.SERVICE_PRINCIPAL]: [
    ServicePrincipalOptions.NAME,
    ServicePrincipalOptions.ID,
    ServicePrincipalOptions.SOURCE_IP,
    ServicePrincipalOptions.DESTINATION_IP,
    ServicePrincipalOptions.SOURCE_USER,
    ServicePrincipalOptions.DESTINATION_USER,
    ServicePrincipalOptions.ALERT_NAME,
  ],
  [DetectionRuleType.USER_GROUP]: [
    UserGroupOptions.NAME,
    UserGroupOptions.ID,
    UserGroupOptions.SOURCE_IP,
    UserGroupOptions.DESTINATION_IP,
    UserGroupOptions.SOURCE_USER,
    UserGroupOptions.DESTINATION_USER,
    UserGroupOptions.ALERT_NAME,
  ],
  [DetectionRuleType.AUTOMATION_ACCOUNT]: [
    AutomationAccountOptions.NAME,
    AutomationAccountOptions.ID,
    AutomationAccountOptions.SOURCE_IP,
    AutomationAccountOptions.DESTINATION_IP,
    AutomationAccountOptions.SOURCE_USER,
    AutomationAccountOptions.DESTINATION_USER,
    AutomationAccountOptions.ALERT_NAME,
  ],
  [DetectionRuleType.AUTOMATION_ACCOUNT_HOOK]: [
    AutomationAccountHookOptions.NAME,
    AutomationAccountHookOptions.ID,
    AutomationAccountHookOptions.SOURCE_IP,
    AutomationAccountHookOptions.DESTINATION_IP,
    AutomationAccountHookOptions.SOURCE_USER,
    AutomationAccountHookOptions.DESTINATION_USER,
    AutomationAccountHookOptions.ALERT_NAME,
  ],
  [DetectionRuleType.API]: [
    ApiOptions.NAME,
    ApiOptions.ID,
    ApiOptions.SOURCE_IP,
    ApiOptions.DESTINATION_IP,
    ApiOptions.SOURCE_USER,
    ApiOptions.DESTINATION_USER,
    ApiOptions.ALERT_NAME,
  ],
  [DetectionRuleType.CERTIFICATE_AUTHORITY]: [
    CertificateAuthorityOptions.NAME,
    CertificateAuthorityOptions.SOURCE_IP,
    CertificateAuthorityOptions.DESTINATION_IP,
    CertificateAuthorityOptions.SOURCE_USER,
    CertificateAuthorityOptions.DESTINATION_USER,
    CertificateAuthorityOptions.ALERT_NAME,
  ],
  [DetectionRuleType.BUCKET]: [
    BucketOptions.NAME,
    BucketOptions.SOURCE_IP,
    BucketOptions.DESTINATION_IP,
    BucketOptions.SOURCE_USER,
    BucketOptions.DESTINATION_USER,
    BucketOptions.ALERT_NAME,
  ],
  [DetectionRuleType.BITBUCKET_REPOSITORY]: [
    BitbucketRepositoryOptions.NAME,
    BitbucketRepositoryOptions.ID,
    BitbucketRepositoryOptions.URL,
    BitbucketRepositoryOptions.SOURCE_IP,
    BitbucketRepositoryOptions.DESTINATION_IP,
    BitbucketRepositoryOptions.SOURCE_USER,
    BitbucketRepositoryOptions.DESTINATION_USER,
    BitbucketRepositoryOptions.ALERT_NAME
  ],
  [DetectionRuleType.JIRA_PROJECT]: [
    JiraProjectOptions.NAME,
    JiraProjectOptions.ID,
    JiraProjectOptions.DOMAIN,
    JiraProjectOptions.SOURCE_IP,
    JiraProjectOptions.DESTINATION_IP,
    JiraProjectOptions.SOURCE_USER,
    JiraProjectOptions.DESTINATION_USER,
    JiraProjectOptions.ALERT_NAME
  ],
  [DetectionRuleType.CONFLUENCE_PAGE]: [
    ConfluencePageOptions.NAME,
    ConfluencePageOptions.ID,
    ConfluencePageOptions.URL,
    ConfluencePageOptions.SOURCE_IP,
    ConfluencePageOptions.DESTINATION_IP,
    ConfluencePageOptions.SOURCE_USER,
    ConfluencePageOptions.DESTINATION_USER,
    ConfluencePageOptions.ALERT_NAME
  ],
};

export const edrOptions = {
  [DetectionRuleType.PROCESS]: [
    ProcessOptions.NAME,
    ProcessOptions.PATH,
    ProcessOptions.FULL_PATH,
    ProcessOptions.COMMAND_LINE,
    ProcessOptions.PARENT_NAME,
    ProcessOptions.PARENT_PATH,
    ProcessOptions.PARENT_FULL_NAME,
    ProcessOptions.PARENT_COMMAND_LINE,
    ProcessOptions.USER,
    ProcessOptions.MD5,
    ProcessOptions.SHA256,
  ],
  [DetectionRuleType.FILE]: [
    FileOptions.CREATION_PROCESS_NAME,
    FileOptions.CREATION_PROCESS_PATH,
    FileOptions.CREATION_PROCESS_FULL_PATH,
    FileOptions.CREATION_PROCESS_COMMAND_LINE,
    FileOptions.OPERATION,
    FileOptions.CREATION_PROCESS_USER,
  ],
  [DetectionRuleType.CONNECTION]: [
    ConnectionOptions.SOURCE_PORT,
    ConnectionOptions.DESTINATION_PORT,
    ConnectionOptions.CREATION_PROCESS_NAME,
    ConnectionOptions.CREATION_PROCESS_PATH,
    ConnectionOptions.CREATION_PROCESS_FULL_PATH,
    ConnectionOptions.CREATION_PROCESS_COMMAND_LINE,
    ConnectionOptions.CREATION_PROCESS_USER,
    ConnectionOptions.URL,
    ConnectionOptions.HTTP_USER,
    ConnectionOptions.HTTP_DOWNLOADED_FILE,
    ConnectionOptions.HTTP_UPLOADED_FILE,
    ConnectionOptions.FTP_USER,
    ConnectionOptions.SMB_DOMAIN,
    ConnectionOptions.SMB_SHARE_PATH,
    ConnectionOptions.SMB_USER,
    ConnectionOptions.SSH_USER,
    ConnectionOptions.WMI_EXEC_QUERY,
    ConnectionOptions.TELNET_USER,
    ConnectionOptions.FILE_REMOTE_OPERATION,
    ConnectionOptions.FILE_REMOTE_PATH,
  ],
  [DetectionRuleType.REGISTRY]: [
    RegistryOptions.KEY,
    RegistryOptions.VALUE,
    RegistryOptions.OPERATION,
    RegistryOptions.CREATION_PROCESS_NAME,
    RegistryOptions.CREATION_PROCESS_PATH,
    RegistryOptions.CREATION_PROCESS_FULL_PATH,
    RegistryOptions.CREATION_PROCESS_COMMAND_LINE,
  ],
  [DetectionRuleType.USER_LOGIN]: [
    UserLoginOptions.NAME,
    UserLoginOptions.DOMAIN,
  ],
};

export const xdrOptions = {
  [DetectionRuleType.FILE]: [
    FileOptions.MD5,
    FileOptions.SHA256,
    FileOptions.URL,
  ],
  [DetectionRuleType.CONNECTION]: [
    ConnectionOptions.SOURCE_USER,
    ConnectionOptions.DESTINATION_USER,
    ConnectionOptions.FILE_NAME,
    ConnectionOptions.EMAIL_SUBJECT,
    ConnectionOptions.APPLICATION_NAME,
    ConnectionOptions.KEY_VAULT_NAME,
    ConnectionOptions.ROLE_NAME,
    ConnectionOptions.POLICY_NAME,
    ConnectionOptions.SHARING_LINK_NAME,
    ConnectionOptions.FLOW_NAME,
    ConnectionOptions.URL_NAME,
    ConnectionOptions.SSH_KEY_NAME,
    ConnectionOptions.LAUNCH_TEMPLATE_NAME,
    ConnectionOptions.SERVICE_PRINCIPAL_NAME,
    ConnectionOptions.USER_GROUP_NAME,
    ConnectionOptions.AUTOMATION_ACCOUNT_NAME,
    ConnectionOptions.AUTOMATION_ACCOUNT_HOOK_NAME,
    ConnectionOptions.API_NAME,
    ConnectionOptions.CERTIFICATE_AUTHORITY_NAME,
    ConnectionOptions.BUCKET_NAME,
    ConnectionOptions.BITBUCKET_REPOSITORY_NAME,
    ConnectionOptions.JIRA_PROJECT_NAME,
    ConnectionOptions.CONFLUENCE_PAGE_NAME
  ],
  [DetectionRuleType.USER_LOGIN]: [
    UserLoginOptions.SOURCE_USER,
    UserLoginOptions.DESTINATION_USER,
    UserLoginOptions.SOURCE_IP,
    UserLoginOptions.DESTINATION_IP,
    UserLoginOptions.FILE_NAME,
    UserLoginOptions.EMAIL_SUBJECT,
    UserLoginOptions.APPLICATION_NAME,
    UserLoginOptions.KEY_VAULT_NAME,
    UserLoginOptions.ROLE_NAME,
    UserLoginOptions.POLICY_NAME,
    UserLoginOptions.SHARING_LINK_NAME,
    UserLoginOptions.FLOW_NAME,
    UserLoginOptions.URL_NAME,
    UserLoginOptions.SSH_KEY_NAME,
    UserLoginOptions.LAUNCH_TEMPLATE_NAME,
    UserLoginOptions.SERVICE_PRINCIPAL_NAME,
    UserLoginOptions.USER_GROUP_NAME,
    UserLoginOptions.AUTOMATION_ACCOUNT_NAME,
    UserLoginOptions.AUTOMATION_ACCOUNT_HOOK_NAME,
    UserLoginOptions.API_NAME,
    UserLoginOptions.CERTIFICATE_AUTHORITY_NAME,
    UserLoginOptions.BUCKET_NAME,
    ConnectionOptions.BITBUCKET_REPOSITORY_NAME,
    ConnectionOptions.JIRA_PROJECT_NAME,
    ConnectionOptions.CONFLUENCE_PAGE_NAME
  ],
  [DetectionRuleType.EMAIL]: [
    EmailOptions.URL,
  ],
  [DetectionRuleType.APPLICATION]: [
    ApplicationOptions.NAME,
    ApplicationOptions.ID,
    ApplicationOptions.APPLICATION_ADDRESS,
    ApplicationOptions.APPLICATION_ADDRESS,
    ApplicationOptions.SOURCE_IP,
    ApplicationOptions.DESTINATION_IP,
    ApplicationOptions.SOURCE_USER,
    ApplicationOptions.DESTINATION_USER,
    ApplicationOptions.ALERT_NAME,
  ],
  [DetectionRuleType.KEY_VAULT]: [
    KeyVaultOptions.NAME,
    KeyVaultOptions.SOURCE_IP,
    KeyVaultOptions.DESTINATION_IP,
    KeyVaultOptions.SOURCE_USER,
    KeyVaultOptions.DESTINATION_USER,
    KeyVaultOptions.ALERT_NAME,
  ],
  [DetectionRuleType.ROLE]: [
    RoleOptions.NAME,
    RoleOptions.ID,
    RoleOptions.SOURCE_IP,
    RoleOptions.DESTINATION_IP,
    RoleOptions.SOURCE_USER,
    RoleOptions.DESTINATION_USER,
    RoleOptions.ALERT_NAME,
  ],
  [DetectionRuleType.POLICY]: [
    PolicyOptions.NAME,
    PolicyOptions.ID,
    PolicyOptions.RESOURCE_POLICY_TYPE,
    PolicyOptions.SOURCE_IP,
    PolicyOptions.DESTINATION_IP,
    PolicyOptions.SOURCE_USER,
    PolicyOptions.DESTINATION_USER,
    PolicyOptions.ALERT_NAME,
  ],
  [DetectionRuleType.SHARING_LINK]: [
    SharingLinkOptions.NAME,
    SharingLinkOptions.URL,
    SharingLinkOptions.SOURCE_IP,
    SharingLinkOptions.DESTINATION_IP,
    SharingLinkOptions.SOURCE_USER,
    SharingLinkOptions.DESTINATION_USER,
    SharingLinkOptions.ALERT_NAME,
  ],
  [DetectionRuleType.FLOW]: [
    FlowOptions.NAME,
    FlowOptions.ID,
    FlowOptions.URL,
    FlowOptions.SOURCE_IP,
    FlowOptions.DESTINATION_IP,
    FlowOptions.SOURCE_USER,
    FlowOptions.DESTINATION_USER,
    FlowOptions.ALERT_NAME,
  ],
  [DetectionRuleType.URL]: [
    UrlOptions.NAME,
    UrlOptions.URL,
    UrlOptions.SOURCE_IP,
    UrlOptions.DESTINATION_IP,
    UrlOptions.SOURCE_USER,
    UrlOptions.DESTINATION_USER,
    UrlOptions.ALERT_NAME,
  ],
  [DetectionRuleType.SSH_KEY]: [
    SshKeyOptions.NAME,
    SshKeyOptions.SSH_PUBLIC_KEY,
    SshKeyOptions.SOURCE_IP,
    SshKeyOptions.DESTINATION_IP,
    SshKeyOptions.SOURCE_USER,
    SshKeyOptions.DESTINATION_USER,
    SshKeyOptions.ALERT_NAME,
  ],
  [DetectionRuleType.LAUNCH_TEMPLATE]: [
    LaunchTemplateOptions.NAME,
    LaunchTemplateOptions.ID,
    LaunchTemplateOptions.SOURCE_IP,
    LaunchTemplateOptions.DESTINATION_IP,
    LaunchTemplateOptions.SOURCE_USER,
    LaunchTemplateOptions.DESTINATION_USER,
    LaunchTemplateOptions.ALERT_NAME,
  ],
  [DetectionRuleType.SERVICE_PRINCIPAL]: [
    ServicePrincipalOptions.NAME,
    ServicePrincipalOptions.ID,
    ServicePrincipalOptions.SOURCE_IP,
    ServicePrincipalOptions.DESTINATION_IP,
    ServicePrincipalOptions.SOURCE_USER,
    ServicePrincipalOptions.DESTINATION_USER,
    ServicePrincipalOptions.ALERT_NAME,
  ],
  [DetectionRuleType.USER_GROUP]: [
    UserGroupOptions.NAME,
    UserGroupOptions.ID,
    UserGroupOptions.SOURCE_IP,
    UserGroupOptions.DESTINATION_IP,
    UserGroupOptions.SOURCE_USER,
    UserGroupOptions.DESTINATION_USER,
    UserGroupOptions.ALERT_NAME,
  ],
  [DetectionRuleType.AUTOMATION_ACCOUNT]: [
    AutomationAccountOptions.NAME,
    AutomationAccountOptions.ID,
    AutomationAccountOptions.SOURCE_IP,
    AutomationAccountOptions.DESTINATION_IP,
    AutomationAccountOptions.SOURCE_USER,
    AutomationAccountOptions.DESTINATION_USER,
    AutomationAccountOptions.ALERT_NAME,
  ],
  [DetectionRuleType.AUTOMATION_ACCOUNT_HOOK]: [
    AutomationAccountHookOptions.NAME,
    AutomationAccountHookOptions.ID,
    AutomationAccountHookOptions.SOURCE_IP,
    AutomationAccountHookOptions.DESTINATION_IP,
    AutomationAccountHookOptions.SOURCE_USER,
    AutomationAccountHookOptions.DESTINATION_USER,
    AutomationAccountHookOptions.ALERT_NAME,
  ],
  [DetectionRuleType.API]: [
    ApiOptions.NAME,
    ApiOptions.ID,
    ApiOptions.SOURCE_IP,
    ApiOptions.DESTINATION_IP,
    ApiOptions.SOURCE_USER,
    ApiOptions.DESTINATION_USER,
    ApiOptions.ALERT_NAME,
  ],
  [DetectionRuleType.CERTIFICATE_AUTHORITY]: [
    CertificateAuthorityOptions.NAME,
    CertificateAuthorityOptions.SOURCE_IP,
    CertificateAuthorityOptions.DESTINATION_IP,
    CertificateAuthorityOptions.SOURCE_USER,
    CertificateAuthorityOptions.DESTINATION_USER,
    CertificateAuthorityOptions.ALERT_NAME,
  ],
  [DetectionRuleType.BUCKET]: [
    BucketOptions.NAME,
    BucketOptions.SOURCE_IP,
    BucketOptions.DESTINATION_IP,
    BucketOptions.SOURCE_USER,
    BucketOptions.DESTINATION_USER,
    BucketOptions.ALERT_NAME,
  ],
  [DetectionRuleType.BITBUCKET_REPOSITORY]: [
    BitbucketRepositoryOptions.NAME,
    BitbucketRepositoryOptions.ID,
    BitbucketRepositoryOptions.URL,
    BitbucketRepositoryOptions.SOURCE_IP,
    BitbucketRepositoryOptions.DESTINATION_IP,
    BitbucketRepositoryOptions.SOURCE_USER,
    BitbucketRepositoryOptions.DESTINATION_USER,
    BitbucketRepositoryOptions.ALERT_NAME
  ],
  [DetectionRuleType.JIRA_PROJECT]: [
    JiraProjectOptions.NAME,
    JiraProjectOptions.ID,
    JiraProjectOptions.DOMAIN,
    JiraProjectOptions.SOURCE_IP,
    JiraProjectOptions.DESTINATION_IP,
    JiraProjectOptions.SOURCE_USER,
    JiraProjectOptions.DESTINATION_USER,
    JiraProjectOptions.ALERT_NAME
  ],
  [DetectionRuleType.CONFLUENCE_PAGE]: [
    ConfluencePageOptions.NAME,
    ConfluencePageOptions.ID,
    ConfluencePageOptions.URL,
    ConfluencePageOptions.SOURCE_IP,
    ConfluencePageOptions.DESTINATION_IP,
    ConfluencePageOptions.SOURCE_USER,
    ConfluencePageOptions.DESTINATION_USER,
    ConfluencePageOptions.ALERT_NAME
  ],
};

export const commonOptions = {
  [DetectionRuleType.PROCESS]: [
    ProcessOptions.ALERT_NAME,
  ],
  [DetectionRuleType.FILE]: [
    FileOptions.NAME,
    FileOptions.PATH,
    FileOptions.FULL_PATH,
    FileOptions.ALERT_NAME,
  ],
  [DetectionRuleType.CONNECTION]: [
    ConnectionOptions.SOURCE_IP,
    ConnectionOptions.DESTINATION_IP,
    ConnectionOptions.ALERT_NAME,
  ],
  [DetectionRuleType.EMAIL]: [
    EmailOptions.SUBJECT,
    EmailOptions.SENDER,
    EmailOptions.RECEIVER,
    EmailOptions.ATTACHMENT,
    EmailOptions.ALERT_NAME,
  ],
  [DetectionRuleType.USER_LOGIN]: [
    UserLoginOptions.ALERT_NAME,
  ],
};

export const operators = {
  [Operators.IS]: 'is',
  [Operators.CONTAINS]: 'contains',
  [Operators.IS_ONE_OF]: 'any',
};

export enum ProtocolOptions {
  HTTP = 'HTTP/HTTPs',
  FTP = 'FTP',
  SMB = 'SMB',
  SSH = 'SSH',
  WMI = 'WMI',
  TELNET = 'Telnet',
  FILE_REMOTE = 'file_remote',
}

export const ConnectionOptionsProtocol = {
  [ConnectionOptions.URL]: ProtocolOptions.HTTP,
  [ConnectionOptions.HTTP_USER]: ProtocolOptions.HTTP,
  [ConnectionOptions.HTTP_DOWNLOADED_FILE]: ProtocolOptions.HTTP,
  [ConnectionOptions.HTTP_UPLOADED_FILE]: ProtocolOptions.HTTP,
  [ConnectionOptions.FTP_USER]: ProtocolOptions.FTP,
  [ConnectionOptions.SMB_DOMAIN]: ProtocolOptions.SMB,
  [ConnectionOptions.SMB_USER]: ProtocolOptions.SMB,
  [ConnectionOptions.SMB_SHARE_PATH]: ProtocolOptions.SMB,
  [ConnectionOptions.SSH_USER]: ProtocolOptions.SSH,
  [ConnectionOptions.WMI_EXEC_QUERY]: ProtocolOptions.WMI,
  [ConnectionOptions.TELNET_USER]: ProtocolOptions.TELNET,
  [ConnectionOptions.FILE_REMOTE_OPERATION]: ProtocolOptions.FILE_REMOTE,
  [ConnectionOptions.FILE_REMOTE_PATH]: ProtocolOptions.FILE_REMOTE,
};

export type ResourceOptions =
  ProcessOptions
  | FileOptions
  | ConnectionOptions
  | RegistryOptions
  | UserLoginOptions
  | EmailOptions
  | ApplicationOptions
  | KeyVaultOptions
  | RoleOptions
  | PolicyOptions
  | SharingLinkOptions
  | FlowOptions
  | UrlOptions
  | SshKeyOptions
  | LaunchTemplateOptions
  | ServicePrincipalOptions
  | UserGroupOptions
  | AutomationAccountOptions
  | AutomationAccountHookOptions
  | ApiOptions
  | CertificateAuthorityOptions
  | BucketOptions;

export enum FILE_OPERATION_OPTIONS {
  CREATE = 'create',
  READ = 'read',
  WRITE = 'write',
  MOVE = 'move',
  RENAME = 'rename',
  COPY = 'copy',
}

export enum CONNECTION_FILE_REMOTE_OPERATION_OPTIONS {
  CREATE = 'create',
  DELETE = 'rem_delete',
  READ = 'read',
  WRITE = 'write',
  MOVE = 'move',
}

export enum REGISTRY_OPERATION_OPTIONS {
  KEY_CREATED = 'key_created',
  KEY_DELETED = 'key_deleted',
  KEY_RENAMED = 'key_renamed',
  VALUE_CREATED = 'value_created',
  VALUE_DELETED = 'value_deleted',
  VALUE_WRITTEN = 'value_written',
}
